import {callAPI} from '@/store/utils';
import {chaptersToEngineSteps} from '@/tours/catalog';
import en from '@/data/tracking/index.js'; // eslint-disable-line

const FYGR_COMPANY = 'FYGR_COMPANY';

// Journey progress (V2) — dernière progression poussée à HubSpot par company.
// Évite de re-POST des valeurs identiques à chaque rechargement de la checklist,
// et sert de mémoire de transition (non-complété → complété) pour l'event Segment.
const lastOnboardingSync = {};

export default {
  namespaced: true,
  state: {
    // Server-driven guide steps (getStartOnboarding) + their "done" state.
    chapters: [],
    // Flags racine de l'onboarding (getStartOnboarding → result.onboarding) :
    //   - isStartGuide : le guide a-t-il déjà été démarré (le back le passe à
    //     true automatiquement dès qu'un chapitre est complété).
    //   - completed / dismissed : onboarding terminé / désactivé.
    guideMeta: {id: null, isStartGuide: false, completed: false, dismissed: false},
    completedChapter: '',
    // Chapters offered to a company at onboarding (availableChapters endpoint),
    // used to build the priority cards.
    companyOnboardingChapters: [],
  },
  mutations: {
    setChapters(state, chapters) {
      state.chapters =
        chapters.sort((a, b) => {
          if (a.order > b.order) return 1;
          if (a.order < b.order) return -1;
          return 0;
        }) || [];
    },
    markChapterCompleted(state, chapter) {
      const chapters = [...state.chapters];
      const chapterIdx = chapters.findIndex((c) => c.condition_key === chapter);
      if (chapterIdx !== -1) {
        chapters[chapterIdx].completed = true;
      } else if (chapter === 'guide') {
        chapters.push({condition_key: 'guide', completed: true});
      }
      state.chapters = [...chapters];
      if (
        state.chapters.length &&
        state.chapters.length ===
          state.chapters.filter((c) => c.completed).length
      ) {
        state.completedChapter = 'guide';
      }
    },
    setChapterCompleted(state, completedChapter) {
      state.completedChapter = completedChapter;
    },
    setCompanyOnboardingChapters(state, companyOnboardingChapters) {
      state.companyOnboardingChapters = companyOnboardingChapters;
    },
    setGuideMeta(state, meta) {
      state.guideMeta = {
        id: meta?.id ?? null,
        isStartGuide: meta?.isStartGuide ?? false,
        completed: meta?.completed ?? false,
        dismissed: meta?.dismissed ?? false,
      };
    },
  },
  actions: {
    async actionGetStartOnboarding({commit, dispatch}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI('POST', 'getStartOnboarding', {companyId});
      // Nouveau shape : result.onboarding = { id, is_start_guide, completed,
      // dismissed, chapters:[…] }. Fallback sur l'ancien shape (result = tableau)
      // au cas où l'API ne serait pas encore à jour.
      const onboarding = res?.error ? null : res?.result?.onboarding ?? null;
      const chapters =
        onboarding?.chapters ?? (Array.isArray(res?.result) ? res.result : []);
      commit('setChapters', chapters);
      commit('setGuideMeta', {
        id: onboarding?.id ?? null,
        isStartGuide: onboarding?.is_start_guide ?? false,
        completed: onboarding?.completed ?? false,
        dismissed: onboarding?.dismissed ?? false,
      });
      // Journey progress (V2) → HubSpot. Ce reload est l'état d'autorité du back
      // (chargement initial ET après chaque tuto via completion-sync), il capte
      // donc aussi les chapitres complétés hors tuto (ex. ajout de catégories).
      dispatch('actionSyncOnboardingProgressToHubSpot');
    },
    // Pousse la progression « Prise en main » (socle) comme propriétés de compte
    // HubSpot : % de complétion + drapeau « parcours terminé ». Non bloquant.
    actionSyncOnboardingProgressToHubSpot({state, dispatch}) {
      try {
        const companyId = localStorage.getItem(FYGR_COMPANY);
        if (!companyId) return;
        // Même dénominateur que l'écran de fin : le SOCLE uniquement (les modules
        // « Pour aller plus loin » ne comptent pas dans la prise en main).
        const socle = chaptersToEngineSteps(state.chapters);
        const total = socle.length;
        const done = socle.filter((c) => c.completed).length;
        const pct = total ? Math.round((done / total) * 100) : 0;
        const completed =
          state.guideMeta?.completed === true || (total > 0 && done === total);

        const prev = lastOnboardingSync[companyId];
        // Rien de nouveau depuis le dernier push → on n'appelle pas l'API.
        if (prev && prev.pct === pct && prev.completed === completed) return;
        // Pas encore de progression réelle : on mémorise la base sans POST inutile
        // (la 1re vraie avancée déclenchera le push).
        if (!prev && pct === 0 && !completed) {
          lastOnboardingSync[companyId] = {pct, completed};
          return;
        }

        const properties = {
          onboarding_checklist_completion_pct: pct,
          onboarding_journey_completed: completed,
        };
        if (completed) {
          properties.onboarding_journey_completed_at = new Date().toISOString();
        }
        dispatch(
          'tracking/actionUpdateHubSpotObject',
          {HSObj: {type: 'company', properties}},
          {root: true},
        );

        // Event Segment « une seule fois » : transition non-complété → complété
        // OBSERVÉE dans cette session (pas au 1er chargement d'un compte déjà
        // terminé). Le drapeau HubSpot, lui, est posé de façon idempotente.
        if (
          completed &&
          prev &&
          prev.completed === false &&
          window.analytics &&
          typeof window.analytics.track === 'function'
        ) {
          window.analytics.track(en.EN_ONBOARDING_JOURNEY_COMPLETED.name, {
            ...en.EN_ONBOARDING_JOURNEY_COMPLETED.properties,
            company_id: companyId,
            is_impersonated: !!localStorage.getItem('FYGR_PRETENDED_USER'),
            checklist_completion_pct: pct,
          });
        }

        lastOnboardingSync[companyId] = {pct, completed};
      } catch (err) {
        console.warn('[onboarding] HubSpot progress sync skipped', err);
      }
    },
    // Chapters offered to a company at onboarding (drives the priority cards).
    // Persists to state and returns the raw array; the UI mapping stays in the
    // component.
    async actionGetCompanyOnboardingChapters({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      if (!companyId) return [];
      try {
        const res = await callAPI(
          'GET',
          `company/${companyId}/availableChapters`,
        );
        // The array can arrive at the top level or nested under
        // result / result.chapters / chapters — pick whichever holds it.
        let chapters = [];
        if (res?.error) chapters = [];
        else if (Array.isArray(res)) chapters = res;
        else if (Array.isArray(res.result)) chapters = res.result;
        else if (Array.isArray(res.result?.chapters))
          chapters = res.result.chapters;
        else if (Array.isArray(res.chapters)) chapters = res.chapters;
        commit('setCompanyOnboardingChapters', chapters);
        return chapters;
      } catch (err) {
        console.warn('[availableChapters] fetch failed', err);
        commit('setCompanyOnboardingChapters', []);
        return [];
      }
    },
    // Upsert the chapters the user picked at onboarding (the priority cards).
    // Body: {chapters: [<chapterId>, ...]}.
    async actionUpsertCompanyOnboardingChapters(_, chapters = []) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/companyOnboardingChapters`,
        {chapters},
      );
      return res;
    },
    // Toggle the "don't suggest onboarding chapters again" choice.
    async actionDismissCompanyOnboardingChapters(_, {dismissed} = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/companyOnboardingChapters/dismiss`,
        {dismissed},
      );
      return res;
    },
    // Mark a single onboarding chapter as completed.
    async actionCompleteCompanyOnboardingChapter(_, chapterId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const res = await callAPI(
        'POST',
        `company/${companyId}/companyOnboardingChapter/${chapterId}/complete`,
      );
      return res;
    },
    actionShowChapterCompleted({commit}, chapter) {
      commit('setChapterCompleted', chapter);
    },
    actionSetChapterCompleted({commit}, chapter) {
      commit('markChapterCompleted', chapter);
    },
    async actionSetOnboardingDismissed({commit, state}, params = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const dismissed = params.dismissed ?? true;
      const res = await callAPI('POST', 'setOnboardingDismissed', {
        ...params,
        dismissed,
        companyId,
      });
      if (!res?.error) {
        // On garde les chapitres (pour la checklist) et on reflète le flag.
        commit('setGuideMeta', {...state.guideMeta, dismissed});
      }
      return res;
    },
  },
};
